import { Icon, IconButton, Menu, Portal } from '@chakra-ui/react'
import { useTranslation } from '@infraloop/i18n'
import { IconType } from 'react-icons'
import { LuEllipsisVertical } from 'react-icons/lu'

/** A single row action. */
export interface RowAction {
  /** Stable value / test id for the item. */
  value: string
  /** Localized label. */
  label: string
  /** Leading icon. */
  icon: IconType
  /** Render in the danger (delete) style. */
  danger?: boolean
  /** Draw a separator above this item. */
  separatorBefore?: boolean
  /** Handler invoked when the item is selected. */
  onSelect?: () => void
}

/**
 * Trailing row action menu (⋮) for a project or inspection-area row.
 */
const RowActionsMenu = ({
  items,
  size = 32,
}: {
  /** Menu items to render, in order. */
  items: RowAction[]
  /** Square size of the trigger button in pixels. */
  size?: number
}) => {
  // Hooks
  const { t } = useTranslation()

  return (
    <Menu.Root positioning={{ placement: 'bottom-end' }} lazyMount unmountOnExit>
      <Menu.Trigger asChild>
        <IconButton
          variant="ghost"
          w={`${size}px`}
          h={`${size}px`}
          minW={`${size}px`}
          borderRadius="8px"
          color="gray.400"
          _hover={{ bg: 'gray.100', color: 'gray.700' }}
          aria-label={t('ui.moreActions')}
          data-testid="row-kebab"
        >
          <Icon as={LuEllipsisVertical} boxSize="18px" />
        </IconButton>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content minW="196px" borderRadius="11px" boxShadow="0 12px 32px rgba(16,24,40,.18)" p="6px">
            {items.map((item) => (
              <div key={item.value}>
                {item.separatorBefore && <Menu.Separator borderColor="gray.100" />}
                <Menu.Item
                  value={item.value}
                  data-testid={`row-action-${item.value}`}
                  onSelect={item.onSelect}
                  cursor="pointer"
                  borderRadius="8px"
                  fontSize="13px"
                  color={item.danger ? 'red.600' : undefined}
                  _hover={item.danger ? { bg: 'red.50' } : undefined}
                >
                  <Icon as={item.icon} boxSize="15px" color={item.danger ? 'red.600' : 'gray.500'} />
                  {item.label}
                </Menu.Item>
              </div>
            ))}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  )
}

export default RowActionsMenu
